/**
 * Detecção de plataforma e de instalação, para o onboarding de "Adicionar à
 * Tela de Início".
 *
 * Decisão de produto (aprovada em 08/09): ensinar a instalar, mas **nunca
 * bloquear o uso no navegador**. Instalar é o modo recomendado e é obrigatório
 * para push no iOS — não é um portão.
 *
 * Puro e testável: recebe `userAgent` e um verificador de display mode, em vez
 * de ler `window` por dentro.
 */

export type Platform = 'ios' | 'android' | 'other';

export type InstallCapability =
  /** iOS: não existe prompt programático. Só instrução do gesto. */
  | { kind: 'instructions'; platform: 'ios' }
  /** Android/Chrome: dá para oferecer um botão de verdade. */
  | { kind: 'prompt'; platform: 'android' }
  /** Já está instalada, ou o navegador não suporta. Não mostrar nada. */
  | { kind: 'none'; reason: 'installed' | 'unsupported' };

export function detectPlatform(userAgent: string): Platform {
  const ua = userAgent.toLowerCase();

  // iPadOS moderno se apresenta como Macintosh; o toque é o que o denuncia.
  const iPadOSModerno = ua.includes('macintosh') && ua.includes('mobile');
  if (/iphone|ipad|ipod/.test(ua) || iPadOSModerno) return 'ios';

  if (ua.includes('android')) return 'android';
  return 'other';
}

/**
 * A PWA está rodando instalada (standalone)?
 *
 * O iOS usa `navigator.standalone`, proprietário e anterior ao padrão; os
 * demais usam a media query. Verificar só um dos dois erra em metade dos
 * aparelhos do grupo.
 */
export function isStandalone(
  matchMedia: (query: string) => { matches: boolean },
  navigatorStandalone?: boolean,
): boolean {
  if (navigatorStandalone === true) return true;
  try {
    return matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
}

export function installCapability(params: {
  platform: Platform;
  standalone: boolean;
  /** `beforeinstallprompt` capturado. Nunca dispara no iOS. */
  hasDeferredPrompt: boolean;
}): InstallCapability {
  if (params.standalone) return { kind: 'none', reason: 'installed' };

  if (params.platform === 'ios') return { kind: 'instructions', platform: 'ios' };

  if (params.platform === 'android' && params.hasDeferredPrompt) {
    return { kind: 'prompt', platform: 'android' };
  }

  return { kind: 'none', reason: 'unsupported' };
}

/**
 * Push depende de instalação no iOS — e só a partir do iOS 16.4.
 *
 * Capability progressiva: quem não puder receber push continua usando o app
 * inteiro. Isto decide o que a interface promete, não o que ela libera.
 */
export function canReceivePush(params: {
  platform: Platform;
  standalone: boolean;
  supportsPushApi: boolean;
}): boolean {
  if (!params.supportsPushApi) return false;
  if (params.platform === 'ios') return params.standalone;
  return true;
}
